import { useEffect, useRef, useState } from 'react';

/**
 * Progress hook: milestone jump + steady 1 %/s crawl.
 *
 * Rules:
 * - Each completed API = one milestone of `100 / totalSteps` %.
 * - When N APIs have returned, the bar jumps to `N × 20 %` (for 5 APIs)
 *   and crawls at 1 %/s from there.
 * - Cap = `N × 20 % + 19 %` (max 99 %).  Bar stops at cap until the
 *   next API returns.
 * - When ALL complete → 100 %.
 *
 * @param completionFlags  One boolean per tracked API / step.
 * @returns `{ percentage, elapsed, isComplete }`
 */
export const useAsymptoticProgress = (
  completionFlags: boolean[]
): { percentage: number; elapsed: number; isComplete: boolean } => {
  const mountTimeRef = useRef(performance.now());
  const prevCompleteRef = useRef(false);
  const prevTotalRef = useRef(0);

  // Persists across effect re-runs so crawl position is never lost
  const currentPctRef = useRef(0);
  const prevCountRef = useRef(0);

  const [percentage, setPercentage] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  const totalSteps = completionFlags.length;
  const completedCount = completionFlags.filter(Boolean).length;
  const isAllComplete =
    totalSteps > 0 ? completedCount === totalSteps : true;

  // ---- reset on new loading cycle ----
  if (
    (prevCompleteRef.current && !isAllComplete) ||
    (prevTotalRef.current === 0 && totalSteps > 0)
  ) {
    mountTimeRef.current = performance.now();
    currentPctRef.current = 0;
    prevCountRef.current = 0;
  }
  prevTotalRef.current = totalSteps;
  prevCompleteRef.current = isAllComplete;

  // ---- detect new completions → jump ----
  if (completedCount > prevCountRef.current) {
    const step = totalSteps > 0 ? 100 / totalSteps : 20;
    const jumpTo = completedCount * step;
    currentPctRef.current = jumpTo;
    prevCountRef.current = completedCount;
  }

  const TAIL_MS = 1200;

  useEffect(() => {
    let rafId: number;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;

    if (isAllComplete) {
      currentPctRef.current = 100;
      setPercentage(100);

      const tick = () => {
        setElapsed(performance.now() - mountTimeRef.current);
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      stopTimer = setTimeout(
        () => cancelAnimationFrame(rafId),
        TAIL_MS
      );
      return () => {
        cancelAnimationFrame(rafId);
        if (stopTimer) clearTimeout(stopTimer);
      };
    }

    // ── Steady crawl at 1 %/s ──
    let lastTime = 0;

    const animate = () => {
      const elapsedMs = performance.now() - mountTimeRef.current;
      const dt =
        lastTime === 0 ? 0 : (elapsedMs - lastTime) / 1000;
      lastTime = elapsedMs;

      // Cap: milestone base + one step minus 1, max 99
      const step = totalSteps > 0 ? 100 / totalSteps : 20;
      const cap = Math.min(
        completedCount * step + step - 1,
        99
      );

      currentPctRef.current = Math.min(
        currentPctRef.current + dt,
        cap
      );

      setPercentage(currentPctRef.current);
      setElapsed(elapsedMs);
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [isAllComplete, totalSteps, completedCount]);

  return { percentage, elapsed, isComplete: isAllComplete };
};
